import { useEffect, useRef, useState } from 'react';

const TRIGGER_THRESHOLD = 70;
const MAX_PULL = 110;
const RESISTANCE = 0.5;

/**
 * Touch-based pull-to-refresh for a scrollable element.
 *
 * Only triggers when the gesture starts at scrollTop=0, so normal scrolling
 * within the container is unaffected.
 *
 * @param {HTMLElement|null} scrollEl - The scroll container to attach to
 * @param {() => Promise<void> | void} onRefresh - Called when threshold met
 * @param {boolean} enabled - Disable to skip attaching listeners
 */
export function usePullToRefresh(scrollEl, onRefresh, enabled = true) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const onRefreshRef = useRef(onRefresh);
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  const refreshingRef = useRef(false);
  useEffect(() => { refreshingRef.current = isRefreshing; }, [isRefreshing]);

  useEffect(() => {
    if (!enabled || !scrollEl) return;

    let startY = 0;
    let isPulling = false;
    let distance = 0;

    const handleTouchStart = (e) => {
      if (scrollEl.scrollTop > 0 || refreshingRef.current) return;
      startY = e.touches[0].clientY;
      isPulling = true;
      distance = 0;
    };

    const handleTouchMove = (e) => {
      if (!isPulling) return;
      // If the user managed to scroll the container down between events, bail.
      if (scrollEl.scrollTop > 0) {
        isPulling = false;
        setPullDistance(0);
        return;
      }
      const delta = e.touches[0].clientY - startY;
      if (delta <= 0) {
        distance = 0;
        setPullDistance(0);
        return;
      }
      distance = Math.min(delta * RESISTANCE, MAX_PULL);
      setPullDistance(distance);
    };

    const handleTouchEnd = async () => {
      if (!isPulling) return;
      isPulling = false;
      const triggered = distance >= TRIGGER_THRESHOLD;
      distance = 0;
      if (triggered) {
        setIsRefreshing(true);
        setPullDistance(TRIGGER_THRESHOLD);
        try {
          await onRefreshRef.current?.();
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
        }
      } else {
        setPullDistance(0);
      }
    };

    scrollEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollEl.addEventListener('touchmove', handleTouchMove, { passive: true });
    scrollEl.addEventListener('touchend', handleTouchEnd, { passive: true });
    scrollEl.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      scrollEl.removeEventListener('touchstart', handleTouchStart);
      scrollEl.removeEventListener('touchmove', handleTouchMove);
      scrollEl.removeEventListener('touchend', handleTouchEnd);
      scrollEl.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [enabled, scrollEl]);

  return { pullDistance, isRefreshing, threshold: TRIGGER_THRESHOLD };
}
